import React from 'react';
import styled from '@emotion/styled';
import { formatFee } from '@/components/domain/seminar/seminarDisplay';
import type { BestCouponPrice } from './useSeminarCoupons';

/*
 * 커머스식 참가비 — `9% 118,300원 ~~130,000원~~` + "○○ 쿠폰 적용 시" 한 줄 (docs/seminar-coupon-spec.md "상세 참가비 칸").
 * 모바일 정보 표(size md)와 PC 요약 카드(size lg)가 같이 쓴다.
 */

const Line = styled.span`
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 6px;
  font-variant-numeric: tabular-nums;
`;

const Rate = styled.span<{ size: 'md' | 'lg' }>`
  font-size: ${({ size }) => (size === 'lg' ? '22px' : '18px')};
  font-weight: 800;
  color: #e11d48;
`;

const Price = styled.span<{ size: 'md' | 'lg' }>`
  font-size: ${({ size }) => (size === 'lg' ? '26px' : '20px')};
  font-weight: 800;
  line-height: 1.3;
  color: #111827;
`;

const Was = styled.s<{ size: 'md' | 'lg' }>`
  font-size: ${({ size }) => (size === 'lg' ? '14px' : '13px')};
  color: #9ca3af;
`;

const Hint = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: #6b7280;
  word-break: keep-all;
`;

interface CouponFeeProps {
  best: BestCouponPrice;
  listPrice: number;
  size?: 'md' | 'lg';
  /** 아직 안 받은 쿠폰 기준일 때 덧붙이는 말 — 모바일 "쿠폰은 아래에서 받을 수 있어요" */
  claimHint?: string;
}

const CouponFee: React.FC<CouponFeeProps> = ({ best, listPrice, size = 'md', claimHint }) => (
  <>
    <Line>
      {best.rate > 0 && <Rate size={size}>{best.rate}%</Rate>}
      <Price size={size}>{formatFee(best.price)}</Price>
      <Was size={size} aria-label={`정가 ${formatFee(listPrice)}`}>{formatFee(listPrice)}</Was>
    </Line>
    <Hint>
      {best.couponName} 쿠폰 적용 시{best.claimNeeded && claimHint ? ` · ${claimHint}` : ''}
    </Hint>
  </>
);

export default CouponFee;

/** 이미 결제한 세미나 — "30,000원 결제 ~~40,000원~~" + "쿠폰 적용해 결제했어요" */
export interface PaidPrice {
  amount: number;
  listAmount: number;
  discountAmount: number;
}

export const PaidFee: React.FC<{ paid: PaidPrice; size?: 'md' | 'lg' }> = ({ paid, size = 'md' }) => (
  <>
    <Line>
      <Price size={size}>{formatFee(paid.amount)}</Price>
      {paid.discountAmount > 0 && (
        <Was size={size} aria-label={`정가 ${formatFee(paid.listAmount)}`}>{formatFee(paid.listAmount)}</Was>
      )}
    </Line>
    <Hint>{paid.discountAmount > 0 ? `쿠폰 ${paid.discountAmount.toLocaleString()}원 할인받아 결제했어요` : '결제한 금액이에요'}</Hint>
  </>
);
